import BaseModel from "../models/BaseModel";
import type Device from "../models/Device";
import { ApplicationMode } from "../models/SettingsEnums";
import TreeViewModel, { DragMode, DropResult } from "../models/TreeviewModel";
import { ChannelType, channelTypeInfo, getChannelTypePicture } from "../models/ChannelType";

import Logger from "../Logger";
import TreeMenuIcon from "../components/NavigationTree2/TreeMenuIcon";
import type Consumer from "../models/Consumer";
import type Room from "../models/Room";
import type Page from "../models/Page";
import type Button from "../models/Button";
import { ConnectionType } from "../models/Connection";
import { isUiModeExpert, isUiModeProductAdmin } from "../uiMode";
import type Actor from "../models/Actor";
import { itx } from "../translationItx/itx";
import DeviceTemplate from "../models/DeviceTemplate";
import ConfirmDialog from "../modelDialogs/ConfirmDialog";
import ConnectableObject from "../models/ConnectableObject";
import Scene from "../models/Scene";
import { ensureButtonEditorFacet } from "../../ConceptOne/src/model/C1_Button";

const logger = new Logger();

export default class DeviceTreeViewModel extends TreeViewModel {
  get typedObj(): Device {
    return this.obj as Device;
  }

  get isFolder(): boolean {
    return true;
  }

  get maxChildColumnSizes(): Array<number> {
    if (this.typedObj.isActor && channelTypeInfo(this.typedObj.channelType).isSupply) {
      return [400, 400];
    }
    return [];
  }

  getTagNeeded(): boolean {
    return !channelTypeInfo(this.typedObj.channelType).isSupply;
  }

  get childCollection(): Array<BaseModel> | undefined {
    return this.typedObj.channelsSortedForDisplay({
      buttonChannels: true,
      actorChannels: true,
      environmentChannel: isUiModeExpert() ? true : false,
      temperatureMeasurementChannel: true,
      channelWithoutKnxChannel: true,
      generalChannel: isUiModeExpert() ? true : false,
      specialChannel: isUiModeExpert() ? true : false,
      patchChannels: true,
      longPressChannels: true,
      manualChannel: isUiModeExpert() ? true : false,
      ignoreChannel: false,
      unknownChannel: false,
      statusChannels: true,
      logicChannels: true,
    });
  }

  getIcon(context?: any): any | undefined {
    let url = "";

    const forObjectReference = context == "ObjectReference";

    if (this.obj.className == "Button") {
      url = (this.typedObj as Button).defaultImageSrc;
    }

    if (this.typedObj.image != "") {
      url = this.typedObj.image;
    }

    if (this.obj.className == "Actor") {
      if (channelTypeInfo(this.typedObj.channelType).isSupply) {
        return (
          <div draggable={false} style={{ position: "relative", width: forObjectReference ? undefined : "30px" }}>
            <i
              draggable={false}
              className={`las la-bolt`}
              style={{
                fontSize: "1.5em",
                marginLeft: "-5px",
              }}
            />
          </div>
        );
      } else {
        return (
          <div draggable={false} style={{ position: "relative", width: forObjectReference ? undefined : "30px" }}>
            <i
              draggable={false}
              className={`las la-rotate-90 la-server channelType ${this.typedObj.channelType}`}
              style={{
                fontSize: "1.5em",
                marginLeft: "-5px",
              }}
            />
            {channelTypeTreeviewSubInfo(this.typedObj.channelType, (this.typedObj as ConnectableObject).isDali)}
          </div>
        );
      }
    } else {
      return <img draggable={false} className={`icon navIcon-${2}`} src={url} />;
    }
  }

  // getTitlePrefix(): string | JSX.Element {
  //   return channelTypeTreeviewSubInfo(this.typedObj.channelType, (this.typedObj as ConnectableObject).isDali);
  // }

  getTitlePostfix(): string | JSX.Element {
    if (state.settings.applicationMode === ApplicationMode.ConceptOne) return "";
    const device = this.typedObj;
    if (!device.template && !channelTypeInfo(device.channelType).isSupply && device.channelType != ChannelType.Lan) {
      const imgSrcWarning = "images/warning.png";

      return (
        <div style={{ display: "flex", flexDirection: "row" }}>
          <img
            src={imgSrcWarning}
            data-tooltip-id="tv-tooltip"
            data-tooltip-content={state.translate(
              "deviceTreeViewModel.chooseProductForDevice",
              "Wähle ein Produkt für dieses Gerät aus!",
            )}
            data-tooltip-place="bottom"
            data-tooltip-variant="warning" // no itx
            style={{
              width: "25px",
              height: "25px",
              marginLeft: "10px",
              position: "relative",
            }}
          />
        </div>
      );
    } else {
      return "";
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////

  handleProperties() {
    this.handleOpen();
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  inlineButton_ActorDiagnostics() {
    return (
      <TreeMenuIcon
        onClick={(ev: any) => {
          const id = this.obj.id;

          if (
            state.activePanes.isOpen(id + ":diagnostics") // no itx
          ) {
            state.activePanes.activatePane(id + ":diagnostics"); // no itx
          } else {
            state.activePanes.openPane(this.obj);
          }
          return true;
        }}
        iconName="tasks" // no itx
        tooltipText={itx("deviceTreeViewModel.diagnistic", "Diagnose")}
        objName={this.obj.className + this.obj.fullTag}
        key={this.obj.className + this.obj.fullTag + "diagnose"} // no itx
      />
    );
  }

  handleOpen(): boolean {
    this.typedObj.configTemplate = false;
    const plan = state.activePanes.getActiveItem()?.item;
    const standalone = state.settings.applicationMode === ApplicationMode.ConceptOne;
    const activePlan = !plan?.isDeleted && (plan?.c1?.className === "C1_Floor" || plan?.c1?.className === "C1_Room");
    const button = this.typedObj.isButton && (standalone || activePlan)
      ? ensureButtonEditorFacet(this.typedObj as Button) : undefined;
    if (button) {
      const c1 = state.c1;
      c1.editMode.value = standalone
        ? c1.editMode.value === "interactionConcept" ? "interactionConcept" : "EloConcept"
        : "EloDetail";
      // Explicit opening replaces a pinned editor, unlike ordinary selection changes.
      c1.setSelection(button);
      state.dockManager.openButtonEditor(button);
      c1.modeEditorDocked.value = true;
      return true;
    }
    return super.handleOpen();
  }

  getButtons(): JSX.Element[] | undefined {
    if (state.settings.diagnosticMode && this.typedObj.isActor) {
      return [this.inlineButton_ActorDiagnostics()];
    } else {
      const res = [super.inlineButton_Open("homePane.openDeviceSettings")]; // no itx

      // if (state.etsIsOnline) {
      //   res.push(
      //     <TreeMenuIcon
      //       onClick={() => {
      //         syncWithETS(
      //           {
      //             syncGroupAddresses: true,
      //             syncBuildingStructure: true,
      //             syncAllDevcies: false,
      //             devicesToSync: [this.typedObj],
      //           },
      //           state.projectMetadata.etsPin,
      //         );
      //       }}
      //       iconName="file-download" // no itx
      //       // tooltipKey={tooltipKey}
      //       tooltipText={itx("deviceTreeViewModel.syncETS", "Sync ETS")}
      //       objName="SyncETS" // no itx
      //       key="SyncETS"
      //     />,
      //   );
      // }

      super.inlineButton_Open;
      if (isUiModeProductAdmin() && this.typedObj.template) {
        res.push(
          <TreeMenuIcon
            onClick={() => {
              this.typedObj.configTemplate = true;
              return super.handleOpen();
            }}
            iconName="laptop-code" // no itx
            // tooltipKey={tooltipKey}
            tooltipText={itx("deviceTreeViewModel.editProdConfig", "Edit Product Config")}
            objName="ProductConfig" // no itx
            key="ProductConfig"
          />,
        );
      }

      if (isUiModeExpert() && this.typedObj.isActor) {
        res.push(
          <TreeMenuIcon
            onClick={() => {
              state.dialogManager.openDialog(
                <ConfirmDialog
                  text={itx(
                    "deviceTreeViewModel.sortChannelsAfterFuseQuestion",
                    "Sollen die Kanäle nach Sicherungen sortiert werden?",
                  )}
                  title={itx("deviceTreeViewModel.sortChannelsAfterFuse", "sort channels after fuse")}
                  onOk={() => {
                    (this.typedObj as Actor).reSortAfterFuse();
                    return true;
                  }}
                  onAbort={() => {
                    return true;
                  }}
                  onClose={() => {
                    return true;
                  }}
                />,
                true,
              );
            }}
            iconName="sort-numeric-down" // no itx
            tooltipText={itx("deviceTreeViewModel.sortChannelsAfterFuse", "sort channels after fuse")}
            objName="reSortFuse" // no itx
            key={"reSortFuse"} // no itx
          />,
        );
      }

      if (isUiModeProductAdmin() && this.typedObj.template) {
        res.push(super.inlineButton_Reference(this.typedObj.template, "Navigiere zum Produkt")); // no itx
      }

      return res;
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  canBePastedInteractive(copiedObjClassName: string): boolean {
    if (this.className == "Sensor") {
      if (copiedObjClassName == "Button" || copiedObjClassName == "Consumer") {
        return true;
      }
    } else {
      if (copiedObjClassName == "Actor") {
        return true;
      }
    }
    return false;
  }

  async getDisabledContextMenuItems(): Promise<Array<string>> {
    var res = new Array<string>();

    const copiedObjClassName = state.clipboard.getClassName();
    if (this.obj.className == "Button") {
      if (!copiedObjClassName || (copiedObjClassName != "Consumer" && copiedObjClassName != "Button")) {
        res.push("paste"); // no itx
      }
    } else {
      if (!copiedObjClassName || (copiedObjClassName != "Actor" && copiedObjClassName != "Cabinet")) {
        res.push("paste"); // no itx
      }
    }

    return res;
  }

  get contextMenuItems(): any {
    var subLabelPaste = undefined;

    try {
      subLabelPaste = state.contextMenuManager.clipboardData?.items?.[0]?.name;
    } catch (ex) {
      logger.error(ex);
    }
    return {
      items: [
        this.baseContextMenuItems_Copy(),
        // this.baseContextMenuItems_Cut(),
        this.baseContextMenuItems_Paste(subLabelPaste),
        this.baseContextMenuItems_Delete(),
        this.baseContextMenuItems_Open(),
      ],
    };
  }

  allowDrag(): boolean {
    return true;
  }

  allowDrop(event: any): boolean {
    return true;
  }

  async handleDrop2(mode: DragMode, obj: BaseModel): Promise<DropResult> {
    if (obj.className == "Consumer") {
      // find passender channel
      const consumer = obj as Consumer;
      const page = (consumer.parent as Room)?.page as Page;
      if (this.typedObj.isActor) {
        const actor = this.typedObj as Actor;

        consumer.connections.forEach((c) => {
          if (c.getChannel()) {
            c.delete();
          }
        });
        const channel = actor.findConnectableChannel(ConnectionType.Channel, consumer, "soft"); // soft so dass ein neuer channel erstellt wird wenn keiner gefunden wird
        if (channel && page) {
          if (channel.channelType == ChannelType.Generic) {
            channel.channelType = consumer.channelType;
          }
          const connection = channel.connectObject(page, consumer, ConnectionType.Channel);
          actor.updateAllFromKnxMetadataImmediately();

          // check if its a actor channel, no template is set,
          let device = this.typedObj as Device;
          if (
            device?.isActor &&
            !device.template &&
            device.channelType != consumer.channelType &&
            device.channels.asArray().some((channel2) => channel2.connections.size == 0 && channel2 != channel)
          ) {
            // go througth all channels to check if allready with an other consumer connected, if not ask if channeltype should be set to channeltype of the droped consumer
            state.dialogManager.openDialog(
              <ConfirmDialog
                title={itx("DeviceTreeViewModel.changeChannelType", "Channel Type des Aktors ändern?")}
                text={itx(
                  "DeviceTreeViewModel.changeChannelTypeText",
                  "Möchten Sie den Kanaltyp des Aktors (T1) auf den Kanaltyp des gewählten Consumers (T2) ändern?",
                  {
                    T1: itx("ChannelType." + device.channelType, device.channelType),

                    T2: itx("ChannelType." + consumer.channelType, consumer.channelType),
                  },
                )}
                onOk={() => {
                  device.channelType = consumer.channelType;

                  return true;
                }}
              />,
            );
          }

          return new DropResult(true);
        } else {
          return new DropResult(false, itx("deviceTreeViewModel.noConnectableChannelFound"));
        }
      } else {
        const button = this.typedObj as Button;
        const channel = button.findConnectableChannel(ConnectionType.Button, consumer);
        if (channel && page) {
          if (channel.channelType == ChannelType.Generic) {
            channel.channelType = consumer.channelType;
          }
          const connection = channel.connectObject(page, consumer, ConnectionType.Button);
          button.updateAllFromKnxMetadataImmediately();
          return new DropResult(true);
        } else {
          return new DropResult(false, itx("deviceTreeViewModel.noFreeChannelFound", "keinen freien Kanal gefunden"));
        }
      }

      return new DropResult(
        false,
        itx("deviceTreeViewModel.noAvaiiableConnectionFound", "keine mögliche Verbindung gefunden"),
      );
    } else if (obj.className == "Scene" || obj.className == "Multiplexer") {
      // find passender channel
      const scene = obj as Scene;

      if (this.typedObj.isButton) {
        const button = this.typedObj as Button;
        const page = (button.parent as Room)?.page as Page;
        const channel = button.findConnectableChannel(ConnectionType.Button, scene);
        if (channel) {
          if (channel.channelType == ChannelType.Generic) {
            channel.channelType = scene.channelType;
          }
          const connectionType = scene.className == "Scene" ? ConnectionType.Scene : ConnectionType.Multiplexer;
          const connection = channel.connectObject(page, scene, connectionType);
          button.updateAllFromKnxMetadataImmediately();
          return new DropResult(true);
        } else {
          return new DropResult(false, itx("deviceTreeViewModel.noFreeChannelFound", "keinen freien Kanal gefunden"));
        }
      }

      return new DropResult(
        false,
        itx("deviceTreeViewModel.noAvaiiableConnectionFound", "keine mögliche Verbindung gefunden"),
      );
    } else if (obj.className == "Actor" && this.typedObj.parent?.className == "Cabinet") {
      return (this.typedObj.parent as BaseModel).treeViewModel.handleDrop2(mode, obj);
    } else if (obj.className == "DeviceTemplate" && this.typedObj.parent?.className == "Cabinet") {
      // check first if it is possible to apply the template e.g. if supply no template possible
      if (channelTypeInfo(this.typedObj.channelType).isSupply) {
        return new DropResult(
          false,
          itx(
            "deviceTreeViewModel.noTemplateForSupply",
            "Für Versorgungsgeräte können keine Produkte verwendet werden",
          ),
        );
      }
      const deviceTemplate = obj as DeviceTemplate;
      const device = this.typedObj;
      device.template = deviceTemplate;
      if (device.name == "") {
        device.name = deviceTemplate.name;
      }
      state.selectionManager.selectedObject = device;
      device.updateAllFromKnxMetadata();
      return new DropResult(true);
    } else {
      return super.handleDrop2(mode, obj);
    }
  }
}

export function channelTypeTreeviewSubInfo(channelType: ChannelType, isDali?: boolean) {
  const cp = getChannelTypePicture(channelType);

  if (cp != "") {
    // return (
    // <img
    //   style={{
    //     marginLeft: "auto",
    //     height: "15px",
    //     alignSelf: "center",
    //     opacity: "0.75",
    //   }}
    //   src={cp}
    // />
    // );
  }

  let s = "";
  switch (channelType) {
    case ChannelType.Switch:
      s = "|";
      break;
    case ChannelType.Dimmer:
      s = "%";
      break;
    case ChannelType.TuneableWhite:
      s = "TW"; // no itx
      break;
    case ChannelType.Heating:
      s = "H";
      break;
    case ChannelType.Jalousie:
      s = "J";
      break;
    case ChannelType.Blind:
      s = "B";
      break;

    default:
    // s = "/"
    // break;
  }

  if (s || isDali) {
    return (
      <div
        style={{
          fontSize: "0.6em",
          opacity: 0.6,
          position: "absolute",
          left: "calc(100% - 12px)",
          top: "0px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ height: "50%" }}>{s}</div>
        <div style={{ height: "50%" }}>{isDali ? "D" : ""}</div>
      </div>
    );
  }

  return "";
}
